import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { NotificationType } from '@app/shared/interfaces';
import { ICalFeed, PropertiesService } from '@app/shared/services/properties/properties.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';

@Component({
  selector: 'sbnb-dialog-add-ical',
  templateUrl: 'dialog-add-ical.component.html',
  styleUrls: ['dialog-add-ical.component.scss'],
})
export class DialogAddIcalComponent implements OnInit {
  form: UntypedFormGroup;
  loading = false;
  loadingInitialData = true;
  editMode = false;

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<DialogAddIcalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { propertyId: string; icalFeed?: ICalFeed },
    private propertyService: PropertiesService,
    private toast: ToastNotificationsService
  ) {
    this.editMode = !!this.data.icalFeed;

    this.form = this.fb.group({
      url: [{ value: '', disabled: this.editMode }, [Validators.required, Validators.pattern('https?://.*')]],
      name: [''],
      first_name: [''],
      last_name: [''],
    });

    // If in edit mode, populate the form with existing data
    if (this.editMode && this.data.icalFeed) {
      this.form.patchValue({
        url: this.data.icalFeed.url,
        name: this.data.icalFeed.name,
        first_name: this.data.icalFeed.host?.first_name || '',
        last_name: this.data.icalFeed.host?.last_name || '',
      });
      this.loadingInitialData = false;
    } else {
      // Listen for changes to the URL field to auto-populate the name
      this.form.get('url').valueChanges.subscribe((url: string) => {
        if (url) {
          try {
            const urlObj = new URL(url);
            const domain = urlObj.hostname;

            // Handle special domain mappings
            if (domain.includes('expediapartnercentral.com')) {
              this.form.get('name').setValue('expedia.com');
              return;
            }

            if (domain.includes('ownerreservations.com')) {
              this.form.get('name').setValue('ownerrez.com');
              return;
            }

            if (domain.includes('s5.furnishedfinder.com')) {
              this.form.get('name').setValue('furnishedfinder.com');
              return;
            }

            // Extract the main domain part (e.g., 'airbnb.com' from 'foo.airbnb.com')
            const domainParts = domain.split('.');
            let mainDomain;
            if (domainParts.length >= 2) {
              mainDomain = `${domainParts[domainParts.length - 2]}.${domainParts[domainParts.length - 1]}`;
            } else {
              mainDomain = domain;
            }
            this.form.get('name').setValue(mainDomain);
          } catch (e) {
            // Invalid URL format, don't update the name
          }
        }
      });
    }
  }

  ngOnInit(): void {
    // If not in edit mode, fetch initial host data
    if (!this.editMode) {
      this.propertyService.getNewIcalData(this.data.propertyId).subscribe(
        (response) => {
          this.form.get('first_name').setValue(response.data.first_name);
          this.form.get('last_name').setValue(response.data.last_name);
          this.loadingInitialData = false;
        },
        (error) => {
          console.error('Failed to fetch initial host data', error);
          this.loadingInitialData = false;
        }
      );
    }
  }

  onSubmit() {
    if (this.form.valid) {
      this.loading = true;

      const payload = {
        url: this.editMode ? this.data.icalFeed.url : this.form.value.url,
        name: this.form.value.name,
        first_name: this.form.value.first_name,
        last_name: this.form.value.last_name
      };

      if (this.editMode) {
        this.propertyService.updatePropertyIcal(
          this.data.propertyId,
          this.data.icalFeed.id,
          payload
        ).subscribe(
          (res) => {
            this.loading = false;
            this.toast.open('iCal feed updated successfully', 'Dismiss', NotificationType.Success);
            this.dialogRef.close(true);
          },
          (error) => {
            this.loading = false;
            this.toast.open(error.error.message || 'Failed to update iCal feed', 'Dismiss', NotificationType.Error);
          }
        );
      } else {
        this.propertyService.addPropertyIcal(
          this.data.propertyId,
          payload
        ).subscribe(
          (res) => {
            this.loading = false;
            this.toast.open('iCal feed added successfully', 'Dismiss', NotificationType.Success);
            this.dialogRef.close(true);
          },
          (error) => {
            this.loading = false;
            this.toast.open(error.error.message || 'Failed to add iCal feed', 'Dismiss', NotificationType.Error);
          }
        );
      }
    }
  }

  onCancel() {
    this.dialogRef.close();
  }
}
